import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItem,
  Typography,
  Avatar,
  Stack,
} from "@mui/material";
import {
  Job,
  colorByStatus,
  statusToLabel,
} from "../../pages/playground/types";
// Component receives onJobClick handler to handle job selection

/**
 * List view component for batch jobs
 * Displays jobs in a compact list with SKU first, thumbnails, and status on the right
 */
type GroupedJobs = Record<string, Job[]>;

export default function ClientBatchList({
  jobs,
  groupedJobs = null,
  onJobClick,
  showBulkActions = false,
  selectedJobs = new Set(),
  onJobSelectionChange,
  onSelectAll,
  canSelectJob,
}: {
  jobs: Job[];
  groupedJobs?: GroupedJobs | null;
  onJobClick?: (job: Job) => void;
  showBulkActions?: boolean;
  selectedJobs?: Set<string>;
  onJobSelectionChange?: (jobId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean, allJobs: Job[]) => void;
  canSelectJob?: (job: Job) => boolean;
}) {
  const handleJobClick = (job: Job) => {
    if (showBulkActions) {
      // In bulk mode, only allow selection of jobs that can be selected
      if (!canSelectJob?.(job)) return;

      const isSelected = selectedJobs.has(job.id);
      onJobSelectionChange?.(job.id, !isSelected);
      return;
    }
    onJobClick?.(job);
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSelectAll?.(event.target.checked, jobs);
  };

  // Only consider selectable jobs for "select all" logic
  const selectableJobs = jobs.filter((job) => canSelectJob?.(job) ?? true);
  const isAllSelected =
    selectableJobs.length > 0 &&
    selectableJobs.every((job) => selectedJobs.has(job.id));
  const isIndeterminate =
    selectableJobs.some((job) => selectedJobs.has(job.id)) && !isAllSelected;

  // Helper to render the existing list structure for a given set of jobs
  const renderJobList = (listJobs: Job[]) => (
    <List sx={{ width: "100%", py: 0 }}>
      {showBulkActions && listJobs.length > 0 && (
        <ListItem sx={{ py: 0.5, backgroundColor: "grey.50", minHeight: 40 }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAllChange}
              size="small"
              sx={{ mr: 1, p: 0.5 }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: "medium", fontSize: "0.875rem" }}
            >
              Select All (
              {listJobs.filter((job) => canSelectJob?.(job) ?? true).length}{" "}
              available)
            </Typography>
          </Box>
        </ListItem>
      )}

      {listJobs.map((job, index) => (
        <Box key={job.id}>
          <ListItem
            sx={{
              py: 0.5,
              minHeight: 56,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: showBulkActions
                  ? "rgba(0, 0, 0, 0.02)"
                  : "rgba(0, 0, 0, 0.04)",
              },
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => handleJobClick(job)}
          >
            {/* Left side with optional checkbox, SKU and images */}
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              {showBulkActions && (
                <Checkbox
                  checked={selectedJobs.has(job.id)}
                  disabled={!canSelectJob?.(job)}
                  onChange={(e) =>
                    onJobSelectionChange?.(job.id, e.target.checked)
                  }
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{
                    mr: 1,
                    p: 0.5,
                    opacity: canSelectJob?.(job) ? 1 : 0.3,
                  }}
                />
              )}
              {/* SKU */}
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  fontWeight: "medium",
                  color: "text.secondary",
                  mr: 2,
                }}
              >
                {job.master_id || "No SKU"}
              </Typography>

              {/* Thumbnails */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ overflow: "auto", mr: 1 }}
              >
                {job.thumbnails?.length ? (
                  job.thumbnails.map((img, imgIndex) => (
                    <Avatar
                      key={imgIndex}
                      variant="rounded"
                      src={img}
                      alt={`${job.title || "Product"} ${imgIndex + 1}`}
                      sx={{ width: 40, height: 40, borderRadius: 0.5 }}
                    />
                  ))
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 0.5,
                      fontSize: "0.65rem",
                      bgcolor: "grey.200",
                      color: "grey.600",
                    }}
                  >
                    No img
                  </Avatar>
                )}
              </Stack>
            </Box>

            {/* Status chip and Open button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={statusToLabel(job.status, true)}
                size="small"
                sx={{
                  backgroundColor: colorByStatus[job.status],
                  color: "white",
                  fontWeight: "medium",
                }}
              />

              {showBulkActions && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onJobClick?.(job);
                  }}
                  sx={{
                    minWidth: 60,
                    borderColor: "#dee2e6",
                    color: "#495057",
                    "&:hover": {
                      borderColor: "#6c757d",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Open
                </Button>
              )}
            </Box>
          </ListItem>
          {index < listJobs.length - 1 && <Divider component="li" />}
        </Box>
      ))}
    </List>
  );

  const hasGroups = groupedJobs && Object.keys(groupedJobs).length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      {!hasGroups && jobs.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No SKUs available
          </Typography>
        </Box>
      )}

      {hasGroups && groupedJobs
        ? Object.entries(groupedJobs).map(([groupKey, group]) => (
            <Box key={groupKey} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {groupKey} ({group.length})
              </Typography>
              {renderJobList(group)}
            </Box>
          ))
        : renderJobList(jobs)}
    </Box>
  );
}
